import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { DataTable, type Column } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { LuLifeBuoy } from "react-icons/lu";
import { PageHeader } from "@/components/ui/page-header";

interface Ticket {
  id: string;
  userId: string;
  userEmail: string | null;
  inboxId: string | null;
  subject: string;
  category: string;
  status: string;
  priority: string | null;
  createdAt: string;
  updatedAt: string;
}

export function SupportPage() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<string>("");
  const [activeId, setActiveId] = useState<string | null>(null);

  const ticketsQuery = useQuery({
    queryKey: ["console", "support", { filter }],
    queryFn: () => api.support.list({ status: filter || undefined }),
  });

  const detailQuery = useQuery({
    queryKey: ["console", "support", "detail", activeId],
    queryFn: () => (activeId ? api.support.get(activeId) : Promise.reject()),
    enabled: !!activeId,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof api.support.update>[1] }) =>
      api.support.update(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["console", "support"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const replyMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { body: string } }) =>
      api.support.reply(id, body),
    onSuccess: () => {
      toast.success("Reply sent");
      queryClient.invalidateQueries({ queryKey: ["console", "support", "detail"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const columns: Column<Ticket>[] = [
    { header: "Subject", cell: (t) => t.subject },
    { header: "From", cell: (t) => t.userEmail ?? "—" },
    { header: "Status", cell: (t) => <Badge>{t.status}</Badge> },
    { header: "Priority", cell: (t) => t.priority ?? "—" },
    {
      header: "Updated",
      cell: (t) => new Date(t.updatedAt).toLocaleString(),
      className: "text-[var(--color-text-muted)] whitespace-nowrap",
    },
  ];

  return (
    <>
      <PageHeader
        icon={LuLifeBuoy}
        title="Support tickets"
        description={ticketsQuery.data ? `${ticketsQuery.data.total} tickets` : undefined}
      />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-[1fr,420px] gap-6">
        <div className="space-y-4">
          <div className="inline-flex gap-1 rounded-md border border-[var(--color-border)] p-0.5 bg-[var(--color-bg)]">
            {["", "open", "pending", "closed"].map((s) => (
              <button
                key={s || "all"}
                onClick={() => setFilter(s)}
                className={[
                  "px-3 py-1 text-xs rounded transition-colors",
                  filter === s
                    ? "bg-[var(--color-elevated)] text-[var(--color-text)]"
                    : "text-[var(--color-text-muted)] hover:text-[var(--color-text)]",
                ].join(" ")}
              >
                {s || "all"}
              </button>
            ))}
          </div>
          <Card>
            <DataTable
              columns={columns}
              rows={ticketsQuery.data?.tickets}
              isLoading={ticketsQuery.isLoading}
              isError={ticketsQuery.isError}
              rowKey={(t) => t.id}
              onRowClick={(t) => setActiveId(t.id)}
              emptyMessage="No tickets."
            />
          </Card>
        </div>

        <Card className="self-start sticky top-4">
          {!activeId && (
            <div className="p-12 text-center text-sm text-[var(--color-text-muted)]">
              Pick a ticket on the left.
            </div>
          )}
          {activeId && detailQuery.isLoading && (
            <div className="p-12 text-center text-sm text-[var(--color-text-muted)]">
              Loading…
            </div>
          )}
          {detailQuery.data && (
            <TicketDetail
              data={detailQuery.data}
              onUpdate={(body) =>
                updateMutation.mutate({ id: detailQuery.data!.ticket.id, body })
              }
              onReply={(body) =>
                replyMutation.mutate({ id: detailQuery.data!.ticket.id, body: { body } })
              }
              isReplying={replyMutation.isPending}
            />
          )}
        </Card>
      </div>
    </>
  );
}

function TicketDetail({
  data,
  onUpdate,
  onReply,
  isReplying,
}: {
  data: NonNullable<Awaited<ReturnType<typeof api.support.get>>>;
  onUpdate: (body: { status?: string; priority?: string }) => void;
  onReply: (body: string) => void;
  isReplying: boolean;
}) {
  const [reply, setReply] = useState("");
  const t = data.ticket;
  return (
    <div>
      <div className="px-5 py-4 border-b border-[var(--color-border)]">
        <p className="text-sm font-semibold text-[var(--color-text)]">{t.subject}</p>
        <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
          From {t.userEmail ?? t.userId}
        </p>
        <div className="mt-3 flex gap-2">
          <select
            value={t.status}
            onChange={(e) => onUpdate({ status: e.target.value })}
            className="px-2 py-1 text-xs rounded bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)]"
          >
            <option value="open">open</option>
            <option value="pending">pending</option>
            <option value="closed">closed</option>
          </select>
          <select
            value={t.priority ?? "normal"}
            onChange={(e) => onUpdate({ priority: e.target.value })}
            className="px-2 py-1 text-xs rounded bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)]"
          >
            <option value="low">low</option>
            <option value="normal">normal</option>
            <option value="high">high</option>
            <option value="urgent">urgent</option>
          </select>
        </div>
      </div>
      <div className="px-5 py-3 max-h-72 overflow-auto space-y-3">
        {data.messages.map((m) => (
          <div
            key={m.id}
            className={[
              "rounded-md p-3 text-sm",
              m.sender_kind === "staff"
                ? "bg-[rgba(139,92,246,0.1)] border border-[rgba(139,92,246,0.25)]"
                : "bg-[var(--color-bg)] border border-[var(--color-border)]",
            ].join(" ")}
          >
            <p className="text-[10px] uppercase tracking-wider text-[var(--color-text-subtle)] mb-1">
              {m.sender_kind} · {new Date(m.created_at).toLocaleString()}
            </p>
            <p className="whitespace-pre-wrap text-[var(--color-text)]">{m.body}</p>
          </div>
        ))}
      </div>
      <div className="px-5 py-3 border-t border-[var(--color-border)]">
        <textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder="Type a reply…"
          rows={3}
          className="w-full px-3 py-2 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] placeholder:text-[var(--color-text-subtle)] focus:outline-none focus:border-[rgba(139,92,246,0.6)]"
        />
        <div className="mt-2 flex justify-end">
          <Button
            disabled={!reply || isReplying}
            onClick={() => {
              onReply(reply);
              setReply("");
            }}
          >
            Send reply
          </Button>
        </div>
      </div>
    </div>
  );
}

function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 text-xs rounded-md bg-[var(--color-elevated)] text-[var(--color-text-muted)] border border-[var(--color-border)]">
      {children}
    </span>
  );
}
